/* eslint-disable jsx-a11y/anchor-is-valid */

import { SvgIcon, Text, TextStyles } from '@cinch-labs/design-system'
import { Wrapper } from '@cinch-nx/shared-ui'
import Link from 'next/link'
import { Head } from '@cinch-nx/landing-ui-shared'

import { JargonContent, KeywordList, RelatedJargon } from './components'
import styles from './jargon.module.css'
import { isJargonHubStoryContent } from './utils/is-jargon-hub-story-content'
import { usePrefersReducedMotion } from '@cinch-nx/shared-ui'
import { Skeleton } from '@mui/material'
import { StoryData, useStoryblokState } from '@cinch-nx/data-storyblok'
import { JargonFeatureProps } from './get-static-props'
import { JargonPageStoryContent, JargonPreviewStory } from './types'

const siteUrl = 'https://www.cinch.co.uk/'

export const JargonFeature = ({
  story,
  preview = false,
}: JargonFeatureProps): JSX.Element => {
  const prefersReducedMotion = usePrefersReducedMotion()

  const { content, slug } = useStoryblokState({
    story: story as StoryData<JargonPageStoryContent> & { rels: unknown },
    enableBridge: preview,
  })

  const SEO = content?.SEO?.[0]

  if (!content || !isJargonHubStoryContent(content) || !story) {
    return (
      <article className={styles.page}>
        <Wrapper>
          <Skeleton
            sx={{ marginTop: '24px', marginBottom: '16px' }}
            animation={prefersReducedMotion ? false : 'wave'}
            variant="rectangular"
            width={500}
            height={56}
          />
          <Skeleton
            sx={{ marginBottom: '56px' }}
            animation={prefersReducedMotion ? false : 'wave'}
            variant="rectangular"
            width={300}
            height={30}
          />
          <Skeleton
            animation={prefersReducedMotion ? false : 'wave'}
            variant="rectangular"
            className={styles.skeleton}
          />
        </Wrapper>
      </article>
    )
  } else {
    const { fullTerm, term, question, answer, image, keywords, summary } =
      content

    const reducedRelatedJargon = (
      story.rels as StoryData<JargonPageStoryContent>[]
    )?.map(
      (relatedStory): JargonPreviewStory => ({
        slug: relatedStory.slug ?? '',

        content: {
          summary: relatedStory.content?.summary ?? '',
          question: relatedStory.content?.question ?? '',
          keywords: relatedStory.content?.keywords ?? '',
          fullTerm: relatedStory.content?.fullTerm ?? '',
        },
      }),
    )

    const keywordList = keywords?.split(',').map((p) => p.trim())

    return (
      <>
        <Head
          {...SEO}
          fallbackTitle={`${fullTerm} - cinch`}
          fallbackDescription={summary}
        />
        <article className={styles.page}>
          <Wrapper>
            <div className={styles.header}>
              <Link href={'/jargon'} passHref legacyBehavior>
                <a className={styles.headerLink}>
                  <SvgIcon name="chevron-left" height={12} color="toreaBay" />
                  All Jargon
                </a>
              </Link>
              <Link
                href={`mailto:?subject=${question} - Cinch&body=${siteUrl}/jargon/${slug}`}
                passHref
                legacyBehavior
              >
                <a target="_blank" className={styles.headerLink}>
                  <SvgIcon name="share" height={16} gradient="newGalaxy" />
                  Share by Email
                </a>
              </Link>
            </div>
            <h1 className={styles.fullTerm}>
              {fullTerm}
              {term && ` (${term})`}
            </h1>
            {keywordList && keywordList.length > 0 && (
              <KeywordList items={keywordList} />
            )}

            <JargonContent question={question} answer={answer} image={image} />
            {story.rels && story.rels.length > 0 && reducedRelatedJargon && (
              <>
                <div className={styles.title}>
                  <Text
                    element="h2"
                    type={TextStyles.mainTitle}
                    lineHeight="display"
                  >
                    Related Jargon
                  </Text>
                </div>
                <RelatedJargon items={reducedRelatedJargon} />
              </>
            )}
          </Wrapper>
        </article>
      </>
    )
  }
}
